import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { useAppThunkDispatch } from "redux/store";
import { createAgentMlsId, deleteAgentMlsId, MlsIdWriteError, updateAgentMlsId } from "redux/slice/agent-mls-ids";
import { DialogFrame } from "component/shared/dialog-frame";
import { FormField } from "component/shared/form-fields";
import { OutlinedButton } from "component/shared/outlined-button";
import { AgentMlsIds } from "model/agent-mls-ids";
import { createBody, editBody } from "./edit-body";
import { mlsIdErrorMessage } from "../errors";
import { mlsFeaturedOptOut } from "shared/env";
import { styles } from "./style";

// The one Edit/Set modal for an agent's MLS ID, opened from the profile and from the Featured Listings page.
// Edit changes an existing MLS ID (and, when it's on Featured Listings, that row's Office MLS ID); Set adds the
// board's MLS ID where the agent has none. Both are titled Edit MLS ID, as in the final design. The server applies the
// one-per-agent rules and refuses with a code. Opened from the Featured Listings page, Edit also offers Clear MLS ID,
// which deletes the agent's ID on the board and sets its Featured row to None.

export type MlsIdEditTarget =
	| { kind: "edit"; entryId: number }
	| { kind: "set"; agentId: number; boardId: number };

interface Props extends WithStyles<typeof styles> {
	open: boolean;
	agentMlsIds: AgentMlsIds;
	target: MlsIdEditTarget | null;
	// Edit only: offers Clear MLS ID. The Featured Listings page turns it on; the profile doesn't, as in the design.
	clearable?: boolean;
	onClose: () => void;
	onSaved: (message: string) => void;
}

const Component: React.FC<Props> = ({ open, agentMlsIds, target, clearable, onClose, onSaved, classes = {} }) => {
	const dispatch = useAppThunkDispatch();
	const entry = target && target.kind === "edit" ? agentMlsIds.entries.find((e) => e.id === target.entryId) || null : null;
	const agentId = target ? (target.kind === "edit" ? entry?.agentId : target.agentId) : undefined;
	const boardId = target ? (target.kind === "edit" ? entry?.boardId : target.boardId) : undefined;
	const agent = agentId != null ? agentMlsIds.agents.get(agentId) ?? null : null;
	const board = agentMlsIds.boards.find((b) => b.boardId === boardId);
	const boardName = board?.name || "";
	const link = entry ? agentMlsIds.clientBoardLinks.get(entry.id) : undefined;
	const linked = link?.state === "linked";
	const off = link?.state === "off";
	// On an office-sourced account Featured Listings run on Office MLS IDs, so an agent's ID only ever reaches the bio
	// page and lead routing: no Featured wording and no Office MLS ID.
	const office = agentMlsIds.officeAccount;

	const [mlsId, setMlsId] = useState("");
	const [alternateId, setAlternateId] = useState("");
	const [show, setShow] = useState(true);
	const [confirmClear, setConfirmClear] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	// Fill the fields each time the modal opens on a target.
	useEffect(() => {
		if (open) {
			setMlsId(entry ? entry.mlsId : "");
			setAlternateId(link?.alternateId || "");
			setShow(!off);
			setConfirmClear(false);
			setError(null);
			setSaving(false);
		}
	}, [open, target]);

	if (!target || (target.kind === "edit" && !entry)) {
		return null;
	}

	// Which fields and note apply. Set: a billable agent gets a Featured row, so its Office MLS ID is editable. Edit:
	// only an ID on Featured Listings once carries one. A billable agent's new ID, and an ID that's on Featured Listings
	// or kept off it, gets the Show on Featured Listings Page toggle, so the choice is made before the first save. On
	// Edit, turned off, the server holds the Office MLS ID for when it's turned back on, so the field follows the toggle;
	// on Set it stays editable and its value is held the same way. Where saving reaches everything it should, there's
	// no note.
	const billable = !!agent && agent.billable;
	const togglable = mlsFeaturedOptOut && !office && billable && (target.kind === "set" || linked || off);
	const alternateIdEditable = !office && (target.kind === "set" ? billable : togglable ? show : linked);
	let note: React.ReactNode;
	if (office) {
		note = <Mui.Typography className={classes.note}>Saving updates your bio page and lead routing.</Mui.Typography>;
	} else if (target.kind === "set") {
		note = billable ? null : <Mui.Typography className={classes.note}>You aren't billable, so this code powers your bio page and lead routing only.</Mui.Typography>;
	} else if (linked) {
		note = null;
	} else if (off) {
		note = <Mui.Typography className={classes.note}>{togglable
			? "This MLS ID is kept off the Featured Listings page. Turn on Show on Featured Listings Page to show it there again."
			: "This MLS ID is kept off the Featured Listings page, so saving updates your bio page and lead routing only."}</Mui.Typography>;
	} else {
		note = <Mui.Typography className={classes.warn}>This code isn't linked to a single Featured Listings row, so saving updates your bio page and lead routing only. The Office MLS ID is available once it's linked.</Mui.Typography>;
	}

	const save = async () => {
		const code = mlsId.trim();
		if (!code) {
			setError(mlsIdErrorMessage({ code: "MLS_ID_REQUIRED" }));
			return;
		}
		setSaving(true);
		setError(null);
		try {
			if (target.kind === "set") {
				const created = await dispatch(createAgentMlsId({
					agentMlsId: createBody(target.agentId, target.boardId, code, alternateIdEditable ? alternateId : null, togglable, show),
				})).unwrap();
				// As for an edit, the confirmation follows what the service returns.
				onSaved(created?.optOut === true ? "Saved. It's off the Featured Listings page; your bio page and lead routing still use it."
					: billable && !office ? "Saved to the profile and Featured Listings."
					: "Saved.");
			} else {
				const moved = togglable && show === off;
				const saved = await dispatch(updateAgentMlsId({ id: entry!.id, agentMlsId: editBody(entry!, code, alternateId, office, moved, show) })).unwrap();
				// The confirmation follows what the service returns: one without opt-out ignores the flag and saves a
				// plain edit, which gets the plain message.
				const turnedOff = moved && !show && saved?.optOut === true;
				const turnedOn = moved && show && saved?.optOut === false;
				onSaved(turnedOff ? "Saved. It's off the Featured Listings page; your bio page and lead routing still use it."
					: turnedOn ? "Saved. It's back on the Featured Listings page."
					: linked ? "Saved to the profile and Featured Listings."
					: "Saved to the profile.");
			}
		} catch(e) {
			setError(mlsIdErrorMessage(e as MlsIdWriteError, { mlsId: code, boardName }));
			setSaving(false);
		}
	};

	// Clear is for the agent's ID on Featured Listings or kept off it, as on the design's Featured page.
	const canClear = !!clearable && target.kind === "edit" && (linked || off);
	const clear = async () => {
		if (saving) {
			return;
		}
		setConfirmClear(false);
		setSaving(true);
		setError(null);
		try {
			await dispatch(deleteAgentMlsId({ id: entry!.id })).unwrap();
			onSaved(`${entry!.mlsId} cleared.`);
		} catch(e) {
			setError(mlsIdErrorMessage(e as MlsIdWriteError, { mlsId: entry!.mlsId, boardName }));
			setSaving(false);
		}
	};

	return (
		<>
		<DialogFrame
			open={open}
			onClose={onClose}
			closeDisabled={saving}
			title="Edit MLS ID"
			titleId="mls-id-edit-title"
			notice={error && <Mui.Alert severity="error">{error}</Mui.Alert>}
			actions={
				<>
					{canClear && <Mui.Button className={classes.clear} onClick={() => setConfirmClear(true)} disabled={saving}>Clear MLS ID</Mui.Button>}
					<OutlinedButton onClick={onClose} disabled={saving}>Cancel</OutlinedButton>
					<Mui.Button variant="contained" color="primary" onClick={save} disabled={saving}>Save</Mui.Button>
				</>
			}
		>
			{board?.pending && (
				<Mui.Alert severity="warning" className={classes.field}>
					{boardName} is pending approval. You can set up MLS IDs now, but listings won't display until the board is approved.
				</Mui.Alert>
			)}
			{note}
			<div className={classes.board}>
				<Mui.Typography className={classes.boardLabel}>MLS Board</Mui.Typography>
				<Mui.Typography>{boardName}</Mui.Typography>
			</div>
			<div className={classes.field}>
				<FormField id="mls-id-edit-agent-id" label="Agent MLS ID" value={mlsId} onChange={(event) => setMlsId(event.target.value)} />
			</div>
			{!office && <div className={classes.field}>
				<FormField id="mls-id-edit-office-id" label="Office MLS ID" value={alternateId} disabled={!alternateIdEditable} onChange={(event) => setAlternateId(event.target.value)} />
			</div>}
			{togglable && <Mui.FormControlLabel
				className={classes.field}
				control={<Mui.Switch checked={show} color="primary" onChange={(event) => setShow(event.target.checked)} />}
				label="Show on Featured Listings Page"
			/>}
		</DialogFrame>
		{canClear && (
			<DialogFrame
				open={open && confirmClear}
				onClose={() => setConfirmClear(false)}
				title="Clear MLS ID"
				titleId="mls-id-clear-title"
				actions={
					<>
						<OutlinedButton onClick={() => setConfirmClear(false)}>Cancel</OutlinedButton>
						<Mui.Button variant="contained" color="primary" onClick={clear} disabled={saving}>Clear</Mui.Button>
					</>
				}
			>
				<Mui.Typography>
					Clear {entry!.mlsId} from {agent?.name || "this agent"} on {boardName}? Their {boardName} listings will stop showing on Featured Listings and their agent bio page, and leads will no longer route to them from this ID.
				</Mui.Typography>
			</DialogFrame>
		)}
		</>
	);
};

export const MlsIdEditDialog = withStyles(Component, styles);
